import { type ApolloError, gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useTenant } from "@/providers/session";

import { GET_INTEGRATION_QUERY } from "./get";

type DeleteIntegration = {
  integrationId: string;
};

export const useDeleteIntegration = (): [
  (
    input: DeleteIntegration,
    onSuccess?: () => void,
    onError?: () => void,
  ) => void,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [deleteIntegration, { data, loading, error }] = useMutation(
    DELETE_INTEGRATION_MUTATION,
    {
      refetchQueries: [GET_INTEGRATION_QUERY],
      optimisticResponse: {
        deleteIntegration: true,
      },
      update: (cache, { data: { deleteIntegration } }, { variables }) => {
        if (deleteIntegration) {
          const { integrationId } = variables as any;

          cache.modify({
            fields: {
              integrations(existingIntegrations = []) {
                return existingIntegrations.filter(
                  (integration: any) =>
                    integration.integrationId !== integrationId,
                );
              },
            },
          });
        }
      },
    },
  );

  const handleDeleteIntegration = async (
    { integrationId }: DeleteIntegration,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    toast.promise(
      deleteIntegration({
        variables: {
          tenantId,
          integrationId,
        },
      }),
      {
        loading: t("generic_loading"),
        success: () => {
          onSuccess();
          return t("generic_success");
        },
        error: () => {
          onError();
          return t("generic_error", { richColors: true });
        },
      },
    );
  };

  return [
    handleDeleteIntegration,
    {
      data: data?.deleteIntegration,
      loading,
      error,
    },
  ];
};

export const DELETE_INTEGRATION_MUTATION = gql`
  mutation DeleteIntegration(
    $tenantId: String!
    $integrationId: String!
  ) {
    deleteIntegration(
      tenantId: $tenantId
      integrationId: $integrationId
    )
  }
`;
